import { useState, useRef, useCallback } from 'react';
import { Camera, X, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useUploadFile } from '@/features/delivery-proof/api/deliveryProofApi';
import { getAttachmentErrorMessage } from '@/features/delivery-proof/utils/attachments';

interface FileDropzoneProps {
  deliveryProofId: string;
  value?: string;
  onChange: (fileUrl: string | undefined) => void;
  accept?: string;
  disabled?: boolean;
  label?: string;
  /** Mode compact : carré avec icône seulement */
  compact?: boolean;
  /** Icône custom à afficher en mode compact (remplace l'icône Upload par défaut) */
  icon?: React.ReactNode;
}

export const FileDropzone = ({
  deliveryProofId,
  value,
  onChange,
  accept = 'image/png, image/jpeg, application/pdf',
  disabled = false,
  label,
  compact = false,
  icon,
}: FileDropzoneProps) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { mutateAsync: uploadFile, isLoading: isUploading } = useUploadFile();

  const upload = useCallback(async (file: File) => {
    try {
      // Photo affichée telle quelle : seule la page 1 d'un PDF serait visible.
      const { fileUrl } = await uploadFile({ id: deliveryProofId, file });
      onChange(fileUrl);
    } catch (error) {
      toast.error(getAttachmentErrorMessage(error));
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [deliveryProofId, onChange, uploadFile]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) upload(file);
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (disabled || isUploading) return;
    const file = e.dataTransfer.files?.[0];
    if (file) upload(file);
  }, [disabled, isUploading, upload]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled && !isUploading) setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  const handleClick = () => {
    if (!disabled && !isUploading) fileInputRef.current?.click();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  // Mode "uploaded" : miniature
  if (value && !isUploading) {
    return (
      <div className="tw-relative tw-group">
        <button
          type="button"
          onClick={() => setShowModal(true)}
          className={`
            tw-border tw-border-gray-200 tw-rounded-xl tw-overflow-hidden
            hover:tw-border-affinicar-primary tw-transition-colors
            ${compact ? 'tw-w-full tw-aspect-[4/3]' : 'tw-w-full tw-h-20'}
          `}
        >
          <img src={value} alt={label || 'Preview'} className="tw-w-full tw-h-full tw-object-cover" />
        </button>
        {/* Bouton supprimer */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onChange(undefined); }}
          disabled={disabled}
          className="tw-absolute tw-top-1 tw-right-1 tw-p-1 tw-bg-white tw-rounded-full tw-shadow-md tw-text-red-500 hover:tw-text-red-700 tw-transition-colors tw-opacity-0 group-hover:tw-opacity-100"
          title="Supprimer"
        >
          <X className="tw-w-4 tw-h-4" />
        </button>
        {label && (
          <span className="tw-block tw-text-xs tw-text-gray-500 tw-mt-1 tw-text-center">{label}</span>
        )}

        {/* Modal agrandissement */}
        {showModal && (
          <div
            className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/75"
            onClick={() => setShowModal(false)}
          >
            <div className="tw-relative tw-max-w-4xl tw-max-h-[90vh] tw-p-4">
              <button
                className="tw-absolute tw-top-6 tw-right-6 tw-text-white hover:tw-text-gray-300 tw-bg-black/50 tw-rounded-full tw-p-2"
                onClick={() => setShowModal(false)}
              >
                <X className="tw-w-6 tw-h-6" />
              </button>
              <img
                src={value}
                alt="Large preview"
                className="tw-max-w-full tw-max-h-[85vh] tw-object-contain"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`
          tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2
          tw-border-2 tw-border-dashed tw-rounded-xl tw-transition-all tw-duration-300
          ${compact ? 'tw-aspect-[4/3] tw-p-2' : 'tw-p-4'}
          ${disabled || isUploading
            ? 'tw-opacity-50 tw-cursor-not-allowed tw-border-gray-200 tw-bg-gray-50'
            : isDragOver
              ? 'tw-border-affinicar-primary tw-bg-affinicar-primary/5 tw-scale-[1.01]'
              : 'tw-border-gray-300 tw-bg-gray-50/50 hover:tw-border-affinicar-primary/50 hover:tw-bg-gray-50 tw-cursor-pointer'
          }
        `}
      >
        {isUploading ? (
          <Loader2 className="tw-w-6 tw-h-6 tw-animate-spin tw-text-affinicar-primary" />
        ) : compact ? (
          icon || <Upload className="tw-w-5 tw-h-5 tw-text-gray-400" />
        ) : (
          <>
            <Camera className="tw-w-6 tw-h-6 tw-text-gray-400" />
            <span className="tw-text-sm tw-text-gray-500 tw-text-center">
              Glissez ou cliquez pour ajouter
            </span>
          </>
        )}
      </div>
      {label && !compact && (
        <span className="tw-block tw-text-xs tw-text-gray-500 tw-mt-1 tw-text-center">{label}</span>
      )}
      {label && compact && (
        <span className="tw-block tw-text-xs tw-text-gray-500 tw-mt-1 tw-text-center">{label}</span>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        disabled={disabled || isUploading}
        className="tw-hidden"
      />
    </div>
  );
};
